import { Shield, MapPin, Users, MessageSquare, ArrowRight } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';

const Services = () => {
  const services = [
    {
      icon: Shield,
      title: 'Emergency Snake Rescue',
      description: 'Professional snake catcher for urgent snake removal from homes, offices, gardens, and public spaces. Snake in bathroom? Snake in kitchen? We handle all emergency situations 24/7.',
      features: ['24/7 Availability', 'Safe Capture', 'All Species'],
      highlight: true,
    },
    {
      icon: MapPin,
      title: 'Snake Relocation',
      description: 'Humane snake relocation to natural habitats. Every rescued snake is safely transported and released away from human settlements.',
      features: ['Natural Habitat', 'Safe Release', 'Documented'],
      highlight: false,
    },
    {
      icon: Users,
      title: 'Snake Awareness Programs',
      description: 'Educational sessions for schools, apartment communities, and corporate offices about snake safety, identification, and what to do when you find a snake.',
      features: ['Schools', 'Corporates', 'Communities'],
      highlight: false,
    },
    {
      icon: MessageSquare,
      title: 'Snake Consultation',
      description: 'Expert advice on snake-proofing your property. Learn to identify common Bangalore snakes and prevent snake entry into your home.',
      features: ['Property Assessment', 'Prevention Tips', 'Species ID'],
      highlight: false,
    },
  ];

  return (
    <section 
      id="services"
      className="py-20 md:py-32 bg-[#F5F3ED]"
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        {/* Section Header */}
        <div className="text-center mb-16">
          <Badge variant="outline" className="mb-4 text-[#2A4B35] border-[#2A4B35]">
            Snake Rescue Services
          </Badge>
          <h2 
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1A1D1A] mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="services-heading"
          >
            Professional Snake Removal Bangalore
          </h2>
          <p className="text-[#4A4D4A] text-lg max-w-2xl mx-auto">
            Complete <strong>snake catcher</strong> services - from emergency rescue to awareness programs
          </p>
        </div>

        {/* Services Grid - Bento Style */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, index) => {
            const IconComponent = service.icon;
            return (
              <Card 
                key={index}
                className={`service-card border rounded-2xl overflow-hidden ${
                  service.highlight 
                    ? 'md:col-span-2 lg:col-span-2 bg-[#2A4B35] border-[#2A4B35] text-white' 
                    : 'bg-white border-[#D8D4C7]'
                }`}
                data-testid={`service-card-${index + 1}`}
              >
                <CardContent className="p-6 md:p-8 h-full flex flex-col">
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-6 ${
                    service.highlight ? 'bg-[#25D366]/20' : 'bg-[#2A4B35]/10'
                  }`}>
                    <IconComponent className={`w-7 h-7 ${
                      service.highlight ? 'text-[#25D366]' : 'text-[#2A4B35]'
                    }`} />
                  </div>
                  
                  <h3 
                    className={`text-xl md:text-2xl font-bold mb-3 ${
                      service.highlight ? 'text-white' : 'text-[#1A1D1A]'
                    }`}
                    style={{ fontFamily: 'Outfit, sans-serif' }}
                  >
                    {service.title}
                  </h3>
                  
                  <p className={`mb-6 leading-relaxed flex-grow ${
                    service.highlight ? 'text-[#C5D4C9]' : 'text-[#4A4D4A]'
                  }`}>
                    {service.description}
                  </p>
                  
                  <div className="flex flex-wrap gap-2 mt-auto">
                    {service.features.map((feature, fIndex) => (
                      <Badge 
                        key={fIndex}
                        variant="secondary"
                        className={`text-xs ${
                          service.highlight 
                            ? 'bg-white/10 text-white border-white/20' 
                            : 'bg-[#EAE7DF] text-[#4A4D4A] border-transparent'
                        }`}
                      >
                        {feature}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* CTA */}
        <div className="mt-12 text-center">
          <a 
            href="#contact"
            className="inline-flex items-center gap-2 text-[#2A4B35] font-semibold hover:gap-3 transition-all"
            data-testid="services-learn-more"
          >
            Have questions? Get in touch
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default Services;
